import { memo, useEffect, useRef } from "react";

import useAppSelector from "../utils/hooks/useAppSelector";

/**
 * Periodically zooms in/out on the followed station while in "auto" mode.
 * Extracted from LiveMap's updateAutoMode, triggered on station or mode
 * changes (mirroring the original being called both directly and from
 * onModeChange).
 */
function AutoModeListener() {
  const auto = useAppSelector((state) => state.auto);
  const config = useAppSelector((state) => state.config);
  const map = useAppSelector((state) => state.map);
  const mode = useAppSelector((state) => state.mode);
  const station = useAppSelector((state) => state.station);
  // Read via refs so the effect only needs to depend on mode/station, the
  // only values that trigger updateAutoMode in the original class.
  const autoRef = useRef(auto);
  const configRef = useRef(config);
  const mapRef = useRef(map);
  // The timeout callback must read the latest station, not the one captured
  // when the effect ran (mirrors reading this.props.station in the original).
  const stationRef = useRef(station);

  useEffect(() => {
    autoRef.current = auto;
  }, [auto]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  useEffect(() => {
    stationRef.current = station;
  }, [station]);

  useEffect(() => {
    const { maxZoomForAutoMode, minZoomForAutoMode } = configRef.current[mode];

    if (!autoRef.current) {
      return undefined;
    }

    if (station) {
      mapRef.current.getView().animate({
        duration: 1000,
        zoom: maxZoomForAutoMode,
      });
    }

    const timeoutId = window.setTimeout(() => {
      const oldStation = stationRef.current;
      const view = mapRef.current.getView();
      const currZoom = view.getZoom();
      const isZoomedOut = currZoom !== maxZoomForAutoMode;

      view.animate({
        duration: 1000,
        zoom:
          oldStation || isZoomedOut ? maxZoomForAutoMode : minZoomForAutoMode,
      });
    }, 14000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mode, station]);

  return null;
}

export default memo(AutoModeListener);
